import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSearchAndSort } from '../../hooks/useSearchAndSort';
import {
  Box,
  CircularProgress,
  Container,
  Typography,
  Paper,
  styled,
  TextField,
  Chip,
  IconButton,
  Tooltip,
} from '@mui/material';
import { Visibility as VisibilityIcon } from '@mui/icons-material';
import DataTable from '../../components/common/DataTable';
import { offersColumns } from '../../constants/tableColumns';
import { getAllOffersCustomized } from '../../services/offerService';
import { toast } from 'react-toastify';

const LoadingContainer = styled(Box)({
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  minHeight: "60vh",
});

const SearchContainer = styled(Box)({
  marginBottom: '20px',
});

const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 0
  }).format(amount);
};

const formatOffer = (offerData) => {
  const offer = offerData.offer || offerData;
  const facility = offer.facilities?.[0] || {};

  const createdAt = offer.createdAt || offer.createDatetime;
  const createdBy = offer.createdBy || offer.createOpName;
  const primaryId = offer.offerId || offer.id;

  return {
    id: primaryId,
    uuid: offer.uuid,
    offerId: primaryId ? `OFFER-${primaryId}` : undefined,
    status: offer.offerStatus || offer.status || "new",
    createDatetime: createdAt,
    submittedDate: createdAt
      ? new Date(createdAt).toLocaleDateString("en-IN", {
          day: "2-digit",
          month: "long",
          year: "numeric",
        })
      : "N/A",
    loanOffered: offer.loanOffered ?? facility.loanOffered,
    loanBreakDown: offer.loanBreakDown,
    rateOfIntrest: offer.rateOfIntrest ?? facility.rateOfIntrest,
    repaymentTenure: offer.repaymentTenure ?? facility.repaymentTenure,
    processingFees: offer.processingFees ?? facility.miscellaneousCharges,
    miscellaneousCharges: offer.miscellaneousCharges ?? facility.miscellaneousCharges,
    offeredSecurity: offer.offeredSecurity,
    securityCoveragePercent: offer.securityCoveragePercent,
    lenderId: offer.lenderId,
    borrowerId: offer.borrowerId,
    createOpName: createdBy
  };
};

const getStatusColor = (status) => {
  switch (status?.toLowerCase()) {
    case 'accepted':
      return 'success';
    case 'rejected':
      return 'error';
    default:
      return 'default';
  }
};

const FIELD_MAP = new Map([
  ['offerId', 'id'],
  ['submittedDate', 'createDatetime'],
  ['status', 'offerStatus'],
]);

const getSortByField = (columnId) => FIELD_MAP.get(columnId) || columnId;

const Offers = () => {
  const navigate = useNavigate();
  const {searchQuery, setSearchQuery, handleSort, searchAndSortParams, sortBy, order } = useSearchAndSort(500);
  const [offers, setOffers] = useState([]);
  const [totalCount, setTotalCount] = useState(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const fetchOffers = useCallback(async () => {
    setLoading(true);
    try {
      const sortByField = searchAndSortParams.sortBy ? getSortByField(searchAndSortParams.sortBy) : undefined;
      const response = await getAllOffersCustomized(page, rowsPerPage, sortByField, searchAndSortParams.order, searchAndSortParams.searchQuery);
      if (response?.statusCode === 200 || response?.responseCode === 200) {
        const list = response?.result?.data || response?.result?.result || response?.data?.content || response?.content || [];
        setOffers(Array.isArray(list) ? list.map(formatOffer) : []);
        const count = Number.isFinite(response?.rowCount)
          ? response.rowCount
          : (Number.isFinite(response?.result?.rowCount) ? response.result.rowCount : null);
        setTotalCount(count);
      } else {
        setOffers([]);
        setTotalCount(null);
      }
    } catch (error) {
      toast.error(error.message);
      setOffers([]);
      setTotalCount(null);
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, searchAndSortParams.searchQuery, searchAndSortParams.sortBy, searchAndSortParams.order]);

  useEffect(() => {
    fetchOffers();
  }, [fetchOffers]);

  const filteredOffers = useMemo(() => {
    if (!Array.isArray(offers)) return [];

    const searchTerm = searchQuery.toLowerCase();
    return offers.filter(offer =>
      offer.offerId?.toLowerCase().includes(searchTerm) ||
      offer.status?.toLowerCase().includes(searchTerm) ||
      offer.lenderId?.toLowerCase().includes(searchTerm) ||
      offer.createOpName?.toLowerCase().includes(searchTerm)
    );
  }, [offers, searchQuery]);

  const handleViewDetails = useCallback((id) => {
    navigate(`/offers/${id}`);
  }, [navigate]);

  const handleChangePage = useCallback((event, newPage) => {
    setPage(newPage);
  }, []);

  const handleChangeRowsPerPage = useCallback((event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  }, []);

  const customCellRenderer = useMemo(() => ({
    offerId: (row) => row.offerId || "N/A",
    loanOffered: (row) => formatCurrency(row.loanOffered || 0),
    rateOfIntrest: (row) => `${row.rateOfIntrest || 0}%`,
    repaymentTenure: (row) => row.repaymentTenure || "N/A",
    processingFees: (row) => formatCurrency(row.processingFees || 0),
    lenderId: (row) => row.lenderId || "N/A",
    createOpName: (row) => row.createOpName || "N/A",
    status: (row) => (
      <Chip
        label={row.status || "N/A"}
        color={getStatusColor(row.status)}
        size="small"
      />
    ),
    submittedDate: (row) => row.submittedDate || "N/A",
    actions: (row) => (
      <Tooltip title="View Details">
        <IconButton
          onClick={() => handleViewDetails(row.id)}
          size="small"
          color="primary"
        >
          <VisibilityIcon />
        </IconButton>
      </Tooltip>
    ),
  }), [handleViewDetails]);

  if (loading) {
    return (
      <LoadingContainer>
        <CircularProgress />
      </LoadingContainer>
    );
  }

  return (
    <Container maxWidth="xl">
      <Typography variant="h4" component="h1" gutterBottom>
        Offers
      </Typography>

      <SearchContainer>
        <TextField
          fullWidth
          variant="outlined"
          placeholder="Search by Offer ID, Status, Lender ID, or Creator..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
      </SearchContainer>

      <Paper
        sx={{
          width: "100%",
          mb: 2,
          overflow: "hidden",
          boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
        }}
      >
        <DataTable
          permissions={{ view: 1, edit: 1, delete: 1 }}
          columns={offersColumns.columns}
          data={filteredOffers}
          loading={loading}
          page={page}
          rowsPerPage={rowsPerPage}
          totalCount={totalCount ?? undefined}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          onViewClick={(row) => handleViewDetails(row.id)}
          onSort={handleSort}
          currentSortBy={sortBy}
          currentOrder={order}
          noDataMessage="No offers found."
          customCellRenderer={customCellRenderer}
        />
      </Paper>
    </Container>
  );
};

export default React.memo(Offers); 